import { Formik } from "formik";
import { useRef, useState, useEffect } from "react";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormCheckBox from "../formFields/CMSFormCheckBox";
import { expressCheckoutPersonalSchema } from "../utils/validations";
import GeneralButton from "../common/GeneralButton";
import { useToast } from "../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import { useCart } from "../contexts/CartContext";
import axios from "../../config/axios";
import ContainerLoading from "../common/loading/ContainerLoading";
import { useGeneral } from "../contexts/GeneralContext";

const STORAGE_KEY = "guestUserDetails";

const ExpressCheckoutPersonalDetailsForm = ({ onSuccess }) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { cart } = useCart();
  const { extraContent } = useGeneral();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    mobile: useRef(null),
  };

  // ✅ Load saved data from localStorage
  const [initialValues, setInitialValues] = useState({
    cartId: cart?.orderCart?._id || "",
    firstName: "",
    lastName: "",
    email: localStorage.getItem("customerEmail") || "",
    mobile: "",
    agreeToCreateAccount: "0",
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setInitialValues((prev) => ({ ...prev, ...parsed }));
      }
    } catch (err) {
      console.error("Failed to parse saved guest data:", err);
    }
  }, []);

  // ✅ Save to localStorage when form values change
  const handleSaveToLocal = (values) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  };

  // ✅ Scroll to first error field
  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  // ✅ Submit guest details API
  const submitGuestDetails = async (values) => {
    try {
      setLoading(true);
      await axios.post("/cart/add-guest-user-details-to-product-order", {
        cartId: cart?.orderCart?._id || "",
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobile: values.mobile,
        agreeToCreateAccount: values.agreeToCreateAccount,
      });

      addToast("Guest details submitted successfully", "success");
      localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      setError("Submission failed, please try again");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="rounded-[15px] relative min-h-[300px]">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-[15px]">
            <ContainerLoading />
          </div>
        )}

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={expressCheckoutPersonalSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setSubmitting(true);
              await submitGuestDetails(values);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {(form) => (
            <div
              onChange={() => handleSaveToLocal(form.values)} // Auto-save on any change
            >
              <div className="2.5xl:mt-9 p-6 md:p-10 2.5xl:p-20 bg-[#DEE2DD]/50 rounded-[15px] relative min-h-[300px]">
                <p className="text-21pt mb-6 md:mb-8 text-green">
                  Add Personal Details
                </p>
                <div className="w-full grid grid-cols-1 gap-8">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div ref={refs.firstName}>
                      <CMSFormInput
                        name="firstName"
                        label="First Name"
                        form={form}
                        required
                      />
                    </div>
                    <div ref={refs.lastName}>
                      <CMSFormInput
                        name="lastName"
                        label="Last Name"
                        form={form}
                        required
                      />
                    </div>
                  </div>

                  {/* Email & Mobile */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div ref={refs.email}>
                      <CMSFormInput
                        name="email"
                        label="Email"
                        form={form}
                        required
                      />
                    </div>
                    <div ref={refs.mobile}>
                      <CMSFormTelephone
                        name="mobile"
                        label="Mobile"
                        smallDescription="(with WhatsApp enabled)"
                        form={form}
                        required
                      />
                    </div>
                  </div>

                  {/* Checkbox */}
                  <CMSFormCheckBox
                    name="agreeToCreateAccount"
                    label="I Agree to create my Nutricub account."
                    form={form}
                    required
                  />

                  <p className="font-mono text-12pt">
                    {extraContent?.expressCheckout?.createAccountMessage ||
                      "By agreeing to create my profile below, I authorize Nuticub to use my personal data for creating a quick profile and sending future marketing and promotional correspondence."}
                  </p>
                  {error && (
                    <div
                      className={`relative  transition-all duration-300 ease-in-out my-3 ${
                        error
                          ? "opacity-100 -translate-y-3"
                          : "opacity-0 -translate-y-2"
                      }`}
                    >
                      {error && <span className="errorMessage">{error}</span>}
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-end justify-between mt-9">
                <GeneralButton
                  onClick={() => navigate(-1)}
                  text="Prev step"
                  title=""
                  iconPosition="Left"
                  bgColor="#ccc"
                  textColor="#000"
                  icon={<TiChevronLeft size={24} className="me-4" />}
                  className="text-24pt"
                />

                <GeneralButton
                  onClick={() => {
                    form.setTouched(
                      Object.keys(form.initialValues).reduce((acc, key) => {
                        acc[key] = true;
                        return acc;
                      }, {})
                    );

                    form.submitForm().then(async () => {
                      const errors = await form.validateForm();
                      if (Object.keys(errors).length > 0) {
                        scrollToFirstError(errors);
                      }
                    });
                  }}
                  text="Next step"
                  type="button"
                  title=""
                  bgColor="#30160E"
                  textColor="#ccc"
                  icon={<TiChevronRight size={24} className="ms-4" />}
                  className="text-24pt"
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ExpressCheckoutPersonalDetailsForm;
